import {
    CART_RECEIVE,
    CART_SHOW_INFO,
    CHECKOUT_SET_DELIVERY,
    CHECKOUT_SET_PAYMENT,
} from '../constants';
import { pushEcommerceEvent, money } from './dataLayer';
import { consumeListContext } from './listContext';

/**
 * Tracks add_to_cart, remove_from_cart and view_cart.
 *
 * Every cart mutation in the site (buy button, quantity change, delete, multi
 * purchase, reorder, drop zone import) ends with the api returning the whole
 * cart and the client dispatching CART_RECEIVE. Diffing the received cart
 * against the previous one therefore covers all of them with one hook,
 * instead of instrumenting every component.
 */

let currency = null;

const toRowMap = (cart) => {
    const rows = (cart && cart.orderRows) || [];
    return rows.reduce((map, row) => {
        map[row.articleNumber] = row;
        return map;
    }, {});
};

const toItem = (row, quantity, withListContext = true) => ({
    item_id: row.articleNumber,
    item_name: row.name,
    price: money(row.unitPriceExcludingVat),
    quantity,
    ...(withListContext ? consumeListContext(row.articleNumber) : {}),
});

const value = (items) =>
    money(
        items.reduce(
            (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
            0
        )
    );

const trackChanges = (previousCart, cart) => {
    const previousRows = toRowMap(previousCart);
    const currentRows = toRowMap(cart);
    currency = (cart && cart.currencyCode) || currency;

    const added = [];
    const removed = [];

    Object.keys(currentRows).forEach((articleNumber) => {
        const row = currentRows[articleNumber];
        const before = previousRows[articleNumber];
        const delta = row.quantity - (before ? before.quantity : 0);
        if (delta > 0) {
            added.push(toItem(row, delta));
        } else if (delta < 0) {
            removed.push(toItem(row, -delta));
        }
    });

    Object.keys(previousRows).forEach((articleNumber) => {
        if (!currentRows[articleNumber]) {
            const row = previousRows[articleNumber];
            removed.push(toItem(row, row.quantity));
        }
    });

    if (added.length > 0) {
        pushEcommerceEvent('add_to_cart', {
            currency,
            value: value(added),
            items: added,
        });
    }
    if (removed.length > 0) {
        pushEcommerceEvent('remove_from_cart', {
            currency,
            value: value(removed),
            items: removed,
        });
    }
};

const trackViewCart = (cart) => {
    const rows = (cart && cart.orderRows) || [];
    const items = rows.map((row) => toItem(row, row.quantity, false));
    pushEcommerceEvent('view_cart', {
        currency: (cart && cart.currencyCode) || currency,
        value: value(items),
        items,
    });
};

const methodName = (method) =>
    (method && (method.name || (method.id && String(method.id)))) || undefined;

/**
 * add_shipping_info and add_payment_info. They mark the steps inside the
 * checkout, which is what makes it possible to see where customers drop off.
 */
const trackCheckoutStep = (event, cart, extra) => {
    const rows = (cart && cart.orderRows) || [];
    if (rows.length === 0) {
        return;
    }
    const items = rows.map((row) => toItem(row, row.quantity, false));
    pushEcommerceEvent(event, {
        currency: (cart && cart.currencyCode) || currency,
        value: value(items),
        ...extra,
        items,
    });
};

export const trackingMiddleware = (store) => (next) => (action) => {
    // The cart as it looked before the reducer ran. The store is seeded from
    // window.__litium.preloadState without a dispatch, so this - not the first
    // CART_RECEIVE - is what the page actually started with.
    const previousCart = action && action.type === CART_RECEIVE
        ? store.getState().cart
        : null;

    const result = next(action);

    try {
        const { type, payload } = action;
        if (type === CART_RECEIVE) {
            trackChanges(previousCart, payload);
        } else if (type === CART_SHOW_INFO && payload && payload.showInfo) {
            trackViewCart(store.getState().cart);
        } else if (type === CHECKOUT_SET_DELIVERY) {
            trackCheckoutStep('add_shipping_info', store.getState().cart, {
                shipping_tier: methodName(payload && payload.selectedDeliveryMethod),
            });
        } else if (type === CHECKOUT_SET_PAYMENT) {
            trackCheckoutStep('add_payment_info', store.getState().cart, {
                payment_type: methodName(payload && payload.selectedPaymentMethod),
            });
        }
    } catch (e) {
        // Tracking must never break the cart.
    }

    return result;
};
